import { defineStore } from 'pinia'
import { ref } from 'vue'
import { createApi } from "@/services/api"
import Axios from "axios"
import mitt from 'mitt'
import type { Emitter } from 'mitt'

// Define event types
type Events = {
  'site-defaults-loaded': any
  'sidebar-color-updated': string | null
  'logos-updated': { lightLogo: string, darkLogo: string }
  'login-bg-updated': string
  'theme-colors-updated': { theme: string, background: string }
  'theme-updated': string
}

// Create event emitter instance
const emitter: Emitter<Events> = mitt<Events>()

export const useSiteDefaultsStore = defineStore('siteDefaults', () => {
    const logoLight = ref<string>('/images/logo/logo.png')
    const logoDark = ref<string>('/images/logo/logo-dark.png')
    const contactEmail = ref<string>('')
    const contactPhone = ref<string>('')
    const address = ref<string>('')
    const mapLat = ref<string>('')
    const mapLng = ref<string>('')
    const openingHoursWeekday = ref<string>('')
    const openingHoursWeekend = ref<string>('')
    const siteName = ref<string>('')
    const siteDescription = ref<string>('')
    const socialLinks = ref<Array<{icon: string, link: string}>>([])
    const favicon = ref<string>('/images/logo/logo-dark.png')
    const genericPageBanner = ref<string>('/assets/img/shape/5.jpg')
    const openingHourGraphic = ref<string>('/assets/img/shape/10.jpg')
    const homeSettings = ref<{enable_customer_feedback: boolean, enable_reservation: boolean, enable_services: boolean}>({
        enable_customer_feedback: true,
        enable_reservation: true,
        enable_services: true
    })
    const isLoaded = ref<boolean>(false)
    const isLoading = ref<boolean>(false)

    // Actions
    async function loadSiteDefaults() {
        if (isLoading.value) return // Prevent multiple simultaneous calls
        
        isLoading.value = true
        
        try {
            const api = createApi({ handler: Axios, namespace: '/v1' })
            
            // Load general settings
            const generalResponse = await api.get('public/settings?section=general')
            
            if (generalResponse.data && generalResponse.data.status === 'success') {
                const data = generalResponse.data.data
                
                logoLight.value = data.logo_light || '/images/logo/logo.png'
                logoDark.value = data.logo_dark || '/images/logo/logo-dark.png'
                contactEmail.value = data.contact_email || ''
                contactPhone.value = data.contact_phone || ''
                address.value = data.address || ''
                openingHoursWeekday.value = data.opening_hours_weekday || ''
                openingHoursWeekend.value = data.opening_hours_weekend || ''
                siteName.value = data.site_name || 'Upatto'
                siteDescription.value = data.site_description || ''
                socialLinks.value = data.social_links || []
                favicon.value = data.favicon || '/images/logo/logo-icon.png'
                genericPageBanner.value = data.generic_page_banner || '/assets/img/shape/5.jpg'
                openingHourGraphic.value = data.opening_hour_graphic || '/assets/img/shape/10.jpg'
                mapLat.value = data.map_latitude || '0'
                mapLng.value = data.map_longitude || '0'
            }
            
            // Load home settings
            const homeResponse = await api.get('public/settings?section=home')
            
            if (homeResponse.data && homeResponse.data.status === 'success') {
                homeSettings.value = {
                    enable_customer_feedback: homeResponse.data.data.enable_customer_feedback || true,
                    enable_reservation: homeResponse.data.data.enable_reservation || true,
                    enable_services: homeResponse.data.data.enable_services || true
                }
            }
            
            isLoaded.value = true
            
            // Emit event to notify components
            emitter.emit('site-defaults-loaded', {
                logoLight: logoLight.value,
                logoDark: logoDark.value,
                contactEmail: contactEmail.value,
                contactPhone: contactPhone.value,
                homeSettings: homeSettings.value,
                mapLat: mapLat.value,
                mapLng: mapLng.value
            })
        } catch (error) {
            console.error('Failed to load site defaults:', error)
            // Keep default values if API fails
            isLoaded.value = true // Mark as loaded even on error to prevent infinite loading
            
            // Emit event with default values
            emitter.emit('site-defaults-loaded', {
                logoLight: logoLight.value,
                logoDark: logoDark.value,
                mapLat: mapLat.value,
                mapLng: mapLng.value
            })
        } finally {
            isLoading.value = false
        }
    }

    function updateLogos(lightLogo: string, darkLogo: string) {
        logoLight.value = lightLogo
        logoDark.value = darkLogo
        // Emit event for real-time updates
        emitter.emit('logos-updated', { lightLogo, darkLogo })
    }

    function updateTheme(theme: string) {
        // Emit event for real-time updates
        emitter.emit('theme-updated', theme)
    }

    function getLogoLight() {
        return logoLight.value
    }

    function getLogoDark() {
        return logoDark.value
    }

    function getContactEmail() {
        return contactEmail.value
    }

    function getContactPhone() {
        return contactPhone.value
    }

    function getAddress() {
        return address.value
    }

    function getOpeningHours() {
        return {
            weekday: openingHoursWeekday.value,
            weekend: openingHoursWeekend.value
        }
    }

    function getSocialLinks() {
        return socialLinks.value
    }

    function getHomeSettings() {
        return homeSettings.value
    }

    function getSiteName() {
        return siteName.value
    }

    function getSiteDescription() {
        return siteDescription.value
    }

    function getFavicon() {
        return favicon.value
    }

    function getGenericPageBanner() {
        return genericPageBanner.value
    }

    function getOpeningHourGraphic() {
        return openingHourGraphic.value
    }

    function getMapLat() {
        return mapLat.value
    }

    function getMapLng() {
        return mapLng.value
    }

    // Event emitter functions
    function onSiteDefaultsLoaded(callback: (data: any) => void) {
        emitter.on('site-defaults-loaded', callback)
    }

    function onLogosUpdated(callback: (data: { lightLogo: string, darkLogo: string }) => void) {
        emitter.on('logos-updated', callback)
    }

    function onThemeUpdated(callback: (theme: string) => void) {
        emitter.on('theme-updated', callback)
    }

    // Cleanup function
    function offAllEvents() {
        emitter.all.clear()
    }

    return {
        // State
        logoLight,
        logoDark,
        contactEmail,
        contactPhone,
        address,
        openingHoursWeekday,
        openingHoursWeekend,
        siteName,
        siteDescription,
        socialLinks,
        favicon,
        genericPageBanner,
        openingHourGraphic,
        homeSettings,
        isLoaded,
        isLoading,
        
        // Actions
        loadSiteDefaults,
        updateLogos,
        updateTheme,
        
        // Getters
        getLogoLight,
        getLogoDark,
        getContactEmail,
        getContactPhone,
        getAddress,
        getOpeningHours,
        getSocialLinks,
        getHomeSettings,
        getSiteName,
        getSiteDescription,
        getFavicon,
        getGenericPageBanner,
        getOpeningHourGraphic,
        getMapLat,
        getMapLng,
        // Event listeners
        onSiteDefaultsLoaded,
        onLogosUpdated,
        onThemeUpdated,
        offAllEvents
    }
}) 